import React, { useState, useEffect, useRef } from 'react';
import { Image, CheckCircle, Info, RefreshCw, Upload, X, Loader2 } from 'lucide-react';
import { useAppDemo } from '../../context/AppDemoContext';
import { toast } from 'react-hot-toast';

/**
 * LogoSelectionSlide component for selecting a logo to use in the app
 */
const LogoSelectionSlide = ({ registerValidation }) => {
    const { 
        brandData, 
        selectedLogo,
        setSelectedLogo,
        organization
    } = useAppDemo();

    const [logos, setLogos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedLogoIndex, setSelectedLogoIndex] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);
    
    // Get organization ID from URL if not available in context
    const [organizationId, setOrganizationId] = useState(organization?.id || null);
    
    // Effect to get organization ID from URL if not available in context
    useEffect(() => {
        if (!organizationId) {
            const params = new URLSearchParams(window.location.search);
            const urlOrgId = params.get('organizationId') || params.get('org');
            if (urlOrgId) {
                console.log('Found organization ID in URL:', urlOrgId);
                setOrganizationId(urlOrgId);
            }
        }
    }, [organizationId]);

    // Fetch user-uploaded logos
    const fetchUserLogos = async () => {
        try {
            console.log('Fetching user-uploaded logos...');
            
            // Use direct organizationId if organization context isn't available
            const orgId = organization?.id || organizationId;
            
            const url = orgId 
                ? `/api/uploads/user-logos?organizationId=${orgId}`
                : '/api/uploads/user-logos';
            
            console.log(`Requesting logos from: ${url}`);
                
            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include'
            });
            
            if (!response.ok) {
                console.error('Failed to fetch user logos:', response.status, response.statusText);
                return [];
            }
            
            const data = await response.json();
            console.log('User logos response:', data);
            
            if (data.success && Array.isArray(data.logos)) {
                console.log(`Found ${data.logos.length} user logos:`, 
                    data.logos.map(logo => logo.src));
                return data.logos;
            }
            
            return [];
        } catch (error) {
            console.error('Error fetching user logos:', error);
            return [];
        }
    };

    // Effect to load both brand logos and user-uploaded logos
    useEffect(() => {
        const loadLogos = async () => {
            setIsLoading(true);
            
            try {
                // Start by loading any user-uploaded logos
                const userLogos = await fetchUserLogos();
                
                let allLogos = [...userLogos];
                let brandLogos = [];
                
                // Then extract logos from brandData if available
                if (brandData && brandData.logos && Array.isArray(brandData.logos)) {
                    brandData.logos.forEach(logoSet => {
                        if (logoSet.formats && Array.isArray(logoSet.formats)) {
                            logoSet.formats.forEach(format => {
                                if (format.src) {
                                    brandLogos.push({
                                        src: format.src,
                                        type: logoSet.type || 'Default',
                                        theme: logoSet.theme || 'light',
                                        format: format.format || 'png',
                                        width: format.width,
                                        height: format.height
                                    });
                                }
                            });
                        }
                    });
                }
                
                // Merge user logos with brand logos without duplicates
                if (brandLogos.length > 0) {
                    const existingSources = allLogos.map(logo => logo.src);
                    const uniqueBrandLogos = brandLogos.filter(logo => !existingSources.includes(logo.src));
                    allLogos = [...allLogos, ...uniqueBrandLogos];
                }
                
                console.log('All logos loaded:', allLogos);
                
                // Update the logos state
                setLogos(allLogos);
                
                // If we have a previously selected logo, find its index in the current logo list
                if (selectedLogo) {
                    const index = allLogos.findIndex(logo => logo.src === selectedLogo.src);
                    if (index >= 0) {
                        setSelectedLogoIndex(index);
                    } else if (allLogos.length > 0) {
                        // Default to first logo if previously selected not found
                        setSelectedLogoIndex(0);
                        setSelectedLogo(allLogos[0]);
                    }
                } else if (allLogos.length > 0) {
                    // Auto-select first logo if none selected
                    setSelectedLogoIndex(0);
                    setSelectedLogo(allLogos[0]);
                }
            } catch (error) {
                console.error('Error loading logos:', error);
                toast.error('Failed to load logos');
            } finally {
                setIsLoading(false);
            }
        };
        
        loadLogos();
    }, [brandData, organization, selectedLogo, setSelectedLogo]);

    // Handle logo selection
    const handleSelectLogo = (logo, index) => {
        setSelectedLogoIndex(index);
        setSelectedLogo(logo);
    };

    // Handle file input change
    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        
        if (!file) return;
        
        // Check file type
        const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];
        if (!validImageTypes.includes(file.type)) {
            toast.error('Please upload a valid image file (JPEG, PNG, GIF, SVG)');
            return;
        }
        
        // Check file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('File size exceeds 5MB limit');
            return;
        }
        
        // Upload the file
        try {
            setIsUploading(true);
            
            const formData = new FormData();
            formData.append('logo', file);
            
            // Get organization ID from context or URL parameter
            const orgId = organization?.id || organizationId;
            
            /**
             * IMPORTANT: Organization ID is REQUIRED for all logo uploads
             * 
             * The server requires an organization ID for all logo uploads to ensure proper file organization.
             * There are multiple ways to provide the organization ID:
             * 
             * 1. URL query parameter: /api/uploads/simple-logo?organizationId=YOUR_ORG_ID
             * 2. Form data field: formData.append('organizationId', YOUR_ORG_ID)
             * 3. Custom header: headers: { 'x-organization-id': YOUR_ORG_ID }
             * 
             * At least one of these methods must be used, or the upload will fail with a 400 error.
             * 
             * If the organization context is not available when this component loads, consider:
             * - Fetching it from the user's profile
             * - Getting it from the URL parameters
             * - Redirecting to organization selection if none is active
             * - Using a default organization ID (least preferred option)
             */
            
            // Get organization ID from context or URL parameter
            if (orgId) {
                formData.append('organizationId', orgId);
                console.log('Adding organization ID to form data:', orgId);
            }
            
            console.log('Uploading logo to server...', {
                fileSize: file.size,
                fileName: file.name,
                fileType: file.type,
                organizationId: orgId
            });
            
            // First, check if the API is accessible
            console.log('Checking if uploads API is accessible...');
            try {
                const statusResponse = await fetch('/api/uploads/status');
                const statusData = await statusResponse.json();
                console.log('Uploads API status:', statusData);
            } catch (statusError) {
                console.error('Failed to check uploads API status:', statusError);
            }
            
            // Try the simplified upload endpoint first
            try {
                console.log('Trying logo upload...');
                
                // Add organization ID to URL if available - ensure it's included when calling the API
                let uploadUrl = '/api/uploads/simple-logo';
                
                // Make sure organization ID is included in both URL and form data for redundancy
                if (orgId) {
                    uploadUrl = `/api/uploads/simple-logo?organizationId=${encodeURIComponent(orgId)}`;
                    console.log(`Using upload URL with organization ID: ${uploadUrl}`);
                } else {
                    console.warn('No organization ID available for upload URL');
                }
                
                // Double-check form data has organization ID
                if (orgId && !formData.has('organizationId')) {
                    formData.append('organizationId', orgId);
                    console.log('Ensuring organization ID is in form data:', orgId);
                }
                
                const response = await fetch(uploadUrl, {
                    method: 'POST',
                    credentials: 'include',
                    body: formData,
                    // Add headers for better tracing
                    headers: orgId ? {
                        'x-organization-id': orgId
                    } : {}
                });
                
                console.log(`Upload response status: ${response.status} ${response.statusText}`);
                
                // Get the response data whether it's success or error
                const data = await response.json();
                console.log('Upload response data:', data);
                
                if (!response.ok) {
                    throw new Error(data.message || data.error || `Server returned ${response.status}: ${response.statusText}`);
                }
                
                if (!data.success) {
                    throw new Error(data.error || 'Failed to upload logo');
                }
                
                // Create a new logo object
                const newLogo = {
                    src: data.imageUrl || data.logo?.src,
                    type: data.logo?.type || 'Custom Logo',
                    theme: data.logo?.theme || 'light',
                    format: data.logo?.format || file.name.split('.').pop() || 'png',
                    // Add width/height if available, otherwise omit
                    ...(data.logo?.width && { width: data.logo.width }),
                    ...(data.logo?.height && { height: data.logo.height })
                };
                
                console.log('New logo created:', newLogo);
                
                // Immediately add the new logo to the logos array and select it
                // This ensures it's visible even if fetchUserLogos() fails
                const updatedLogos = [...logos];
                updatedLogos.unshift(newLogo); // Add to beginning of array
                
                console.log('Setting logos with newly uploaded logo:', updatedLogos);
                setLogos(updatedLogos);
                
                // Select the newly added logo
                setSelectedLogo(newLogo);
                setSelectedLogoIndex(0);
                
                // Now try to fetch all logos to ensure we have the complete list
                try {
                    console.log('Fetching updated logo list...');
                    const userLogos = await fetchUserLogos();
                    
                    if (userLogos.length > 0) {
                        // Only if we got logos back, update the list
                        const existingBrandLogos = logos.filter(logo => logo.type !== 'Custom Logo');
                        const completeLogos = [...userLogos, ...existingBrandLogos];
                        
                        console.log('Setting complete logo list:', completeLogos);
                        setLogos(completeLogos);
                        
                        // Find the index of the newly uploaded logo in the complete list
                        const newLogoIndex = completeLogos.findIndex(
                            logo => logo.src === newLogo.src
                        );
                        
                        if (newLogoIndex >= 0) {
                            setSelectedLogoIndex(newLogoIndex);
                        }
                    }
                } catch (fetchError) {
                    console.error('Error fetching updated logo list:', fetchError);
                    // We already added the new logo to the list above,
                    // so the user can still see and select it
                }
                
                toast.success('Logo uploaded successfully');
            } catch (error) {
                console.error('Error uploading logo:', error);
                
                // Check if the error is related to organization ID being missing
                if (error.message && (
                    error.message.includes('Organization ID is required') || 
                    error.message.includes('organizationId')
                )) {
                    toast.error('Organization ID is required to upload a logo. Please select an organization first.');
                } else {
                    toast.error(error.message || 'Failed to upload logo');
                }
            } finally {
                setIsUploading(false);
                // Reset the file input
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }
        } catch (error) {
            console.error('Error uploading logo:', error);
            toast.error(error.message || 'Failed to upload logo');
        }
    };

    // Trigger file input click
    const handleUploadClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // Validation for next button
    useEffect(() => {
        registerValidation('logo-selection', selectedLogo !== null);
    }, [selectedLogo, registerValidation]);

    return (
        <div className="slide-container">
            <div className="mb-6 flex items-center">
                <Image className="w-6 h-6 text-yellow-300 mr-2" />
                <h2 className="text-2xl font-medium text-white">App Logo</h2>
            </div>
            
            <p className="text-white/70 mb-6">
                Select the logo that will be used in your app. This logo will appear in the app header and other key locations.
            </p>
            
            {/* Main content - side-by-side layout on larger screens */}
            <div className="flex flex-col lg:flex-row gap-6">
                {/* Left column: Logo selection */}
                <div className="w-full lg:w-2/3">
                    {isLoading ? (
                        <div className="flex justify-center items-center p-12">
                            <RefreshCw className="w-8 h-8 text-white/60 animate-spin" />
                        </div>
                    ) : (
                        <div className="bg-black/30 rounded-lg p-4">
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="text-lg font-medium text-white">Available Logos</h3>
                                
                                {/* Upload button */}
                                <div className="relative">
                                    <button
                                        onClick={handleUploadClick}
                                        disabled={isUploading}
                                        className="flex items-center text-sm bg-yellow-300 hover:bg-yellow-400 text-gray-900 rounded-md px-3 py-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isUploading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="w-4 h-4 mr-1.5" />
                                                Upload Logo
                                            </>
                                        )}
                                    </button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/jpeg,image/png,image/gif,image/svg+xml"
                                        onChange={handleFileChange}
                                        className="hidden"
                                    />
                                </div>
                            </div>
                            <div className="h-[calc(100vh-350px)] overflow-y-auto pr-2">
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                    {logos.map((logo, index) => (
                                        <div 
                                            key={`${logo.src}-${index}`}
                                            className={`relative bg-white/10 rounded-lg p-2 border-2 transition-all cursor-pointer ${
                                                selectedLogoIndex === index 
                                                    ? 'border-yellow-300 shadow-lg scale-105' 
                                                    : 'border-transparent hover:border-white/30'
                                            }`}
                                            onClick={() => handleSelectLogo(logo, index)}
                                        >
                                            <div 
                                                className={`flex items-center justify-center h-20 rounded overflow-hidden ${
                                                    logo.theme === 'dark' ? 'bg-gray-800' : 'bg-white/90'
                                                }`}
                                            >
                                                <img 
                                                    src={logo.src} 
                                                    alt={`${logo.type} logo`}
                                                    className="max-h-16 max-w-full object-contain"
                                                />
                                            </div>
                                            <div className="mt-1 flex justify-between items-center">
                                                <div>
                                                    <h4 className="text-white text-xs font-medium truncate">{logo.type}</h4>
                                                    <p className="text-[10px] text-white/60">{logo.format.toUpperCase()} • {logo.theme}</p>
                                                </div>
                                                {selectedLogoIndex === index && (
                                                    <CheckCircle className="w-4 h-4 text-yellow-300 flex-shrink-0 ml-1" />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Right column: Preview section - always visible */}
                <div className="w-full lg:w-1/3 lg:sticky lg:top-4 lg:self-start">
                    {selectedLogo && (
                        <div className="bg-black/20 p-4 rounded-lg">
                            <h3 className="text-lg font-medium text-white mb-3">Preview</h3>
                            <div className="space-y-4">
                                <div>
                                    <div className="bg-gray-800 rounded-lg p-3 flex items-center justify-center">
                                        <img 
                                            src={selectedLogo.src} 
                                            alt="Selected logo on dark background"
                                            className="max-h-16 max-w-full object-contain"
                                        />
                                    </div>
                                    <p className="text-center text-sm text-white/60 mt-1">Dark Background</p>
                                </div>
                                <div>
                                    <div className="bg-white rounded-lg p-3 flex items-center justify-center">
                                        <img 
                                            src={selectedLogo.src} 
                                            alt="Selected logo on light background"
                                            className="max-h-16 max-w-full object-contain"
                                        />
                                    </div>
                                    <p className="text-center text-sm text-white/60 mt-1">Light Background</p>
                                </div>
                                
                                {/* App header preview */}
                                <div>
                                    <p className="text-sm text-white/70 mb-1">App Header Preview:</p>
                                    <div className="rounded-lg overflow-hidden">
                                        <div className="bg-[#1e6a5c] p-2 flex items-center">
                                            <img 
                                                src={selectedLogo.src} 
                                                alt="Logo in app header"
                                                className="h-8 mr-2 object-contain"
                                            />
                                            <span className="text-white font-medium">
                                                {organization?.name || 'Company Name'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Logo info */}
                                <div className="bg-white/10 rounded p-2 text-xs">
                                    <p className="text-white/80"><span className="font-medium">Type:</span> {selectedLogo.type}</p>
                                    <p className="text-white/80"><span className="font-medium">Format:</span> {selectedLogo.format.toUpperCase()}</p>
                                    {selectedLogo.width && selectedLogo.height && (
                                        <p className="text-white/80">
                                            <span className="font-medium">Dimensions:</span> {selectedLogo.width} × {selectedLogo.height}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    
                    <div className="mt-4 p-3 bg-blue-500/20 border border-blue-500/30 rounded-lg text-white">
                        <div className="flex items-start">
                            <Info className="w-4 h-4 text-blue-300 mr-1.5 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-xs">The logo you select will be used consistently throughout your app interface.</p>
                                <p className="text-xs mt-1">For best results, choose a logo that looks good on both light and dark backgrounds.</p>
                                <p className="text-xs mt-1">You can upload your own custom logo using the "Upload Logo" button.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LogoSelectionSlide; 